import { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TableRow,
  TableCell,
  IconButton,
  TextField
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { WithPermission } from '../../Auth/WithPermission';
import { useAuth } from '../../context/AuthContext';

export function SortableItem({ id, stage, onImmediateDelete, handleDeleteStage, index, onStageNameChange, isNew, setNewBuildStage }) {
  const [isEditing, setIsEditing] = useState(stage === '');
  const [editedName, setEditedName] = useState(stage);
  const inputRef = useRef(null);
  
  const auth = useAuth();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    const newName = editedName.trim();
    
    // If user tried to empty an existing stage
    if (newName === '' && !isNew && stage !== '') {
      setEditedName(stage);
      setIsEditing(false);
      return;
    }

    // Prevent empty names for new stages
    if (newName === '' && isNew) {
      onImmediateDelete(index);
      return;
    }

    // Only proceed if name has actually changed
    if (newName !== stage) {
      const success = onStageNameChange(index, newName);
      if (success) {
        setIsEditing(false);
        setNewBuildStage(false);
      } else {
        setIsDuplicate(true);
        // Keep the field in edit mode
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      if(isNew) {
        onImmediateDelete(index)
      }
      setEditedName(stage);
      setIsEditing(false);
      setIsDuplicate(false);
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const [isDuplicate, setIsDuplicate] = useState(false);

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell sx={{ width: '30px' }}>
        <WithPermission rolesRequired={['admin', 'superadmin']}>
          <IconButton
            {...listeners}
            aria-label="drag"
            sx={{ cursor: 'grab', touchAction: 'none' }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </WithPermission>
      </TableCell>
      { 
        auth.userRole === "useredit" ? 
          (<TableCell>{stage}</TableCell>) 
        : 
          <TableCell onDoubleClick={handleDoubleClick}>
            {isEditing ? (
                <TextField
                  value={editedName}
                  onChange={(e) => {
                    setEditedName(e.target.value);
                    setIsDuplicate(false); // Reset duplicate error when typing
                  }}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  inputRef={inputRef}
                  variant="standard"
                  fullWidth
                  error={isDuplicate}
                  helperText={isDuplicate ? "This name already exists" : ""}
                  slotProps={{
                    htmlInput: { maxLength: 10 }
                  }}
                />
            ) : (
              stage
            )}
          </TableCell>
      }
      <TableCell align="right">
        <WithPermission rolesRequired={['admin', 'superadmin']}>
          <IconButton
            aria-label="edit"
            onClick={handleDoubleClick}
          >
            <EditIcon />
          </IconButton>
        </WithPermission>
        
        <WithPermission rolesRequired={['admin', 'superadmin']}>
          <IconButton
            aria-label="delete"
            onClick={() => {
              if (isNew) {
                onImmediateDelete(index);
              } else {
                handleDeleteStage();
              }}
            }
          >
            <DeleteIcon />
          </IconButton>
        </WithPermission>
      </TableCell>
    </TableRow>
  );
}